import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import { PaperClipIcon, PaperAirplaneIcon, ArrowLeftIcon, ArchiveBoxIcon, PlusIcon, XMarkIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { getThreads, createThread, getThread, replyToThread, archiveThread, getAttachmentUrl } from '../services/messagesService';
import api from '../services/api';
import { getClients } from '../services/clientService';
import Modal from '../components/common/Modal';
import Spinner from '../components/common/Spinner';

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = parseISO(ts);
  const now = new Date();
  const diff = (now - d) / 1000;
  if (diff < 60 * 60 * 24) return format(d, 'h:mm a');
  if (diff < 60 * 60 * 24 * 7) return format(d, 'EEE');
  return format(d, 'MMM d');
};

function NewThreadModal({ isOpen, onClose, onCreated }) {
  const [clientId, setClientId] = useState('');
  const [subject, setSubject] = useState('');
  const [content, setContent] = useState('');
  const queryClient = useQueryClient();

  const { data: clientsData } = useQuery({
    queryKey: ['clients', 'all'],
    queryFn: () => getClients({ limit: 500 }),
    enabled: isOpen,
  });
  const clients = clientsData?.clients ?? [];

  const mutation = useMutation({
    mutationFn: () => createThread({ clientId, subject, content }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['message-threads'] });
      onCreated(data.thread.id);
      onClose();
      setClientId(''); setSubject(''); setContent('');
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to create thread'),
  });

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="New Message" size="md">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Client</label>
          <select className="input" value={clientId} onChange={(e) => setClientId(e.target.value)}>
            <option value="">Select a client…</option>
            {clients.map((c) => (
              <option key={c.id} value={c.id}>{c.firstName} {c.lastName}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">Subject</label>
          <input className="input" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="e.g. Appointment question" />
        </div>
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">First message (optional)</label>
          <textarea className="input" rows={3} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Type your message…" />
        </div>
        <div className="flex justify-end gap-3 pt-2">
          <button onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!clientId || !subject.trim() || mutation.isPending}
            className="btn btn-primary disabled:opacity-60"
          >
            {mutation.isPending ? 'Creating…' : 'Create thread'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function ThreadList({ threads, activeId, onSelect, onNewThread }) {
  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
        <h2 className="font-semibold text-neutral-900 dark:text-neutral-100">Messages</h2>
        <button onClick={onNewThread} className="btn btn-primary text-sm flex items-center gap-1.5 py-1.5 px-3">
          <PlusIcon className="h-4 w-4" /> New
        </button>
      </div>
      <div className="overflow-y-auto flex-1">
        {threads.length === 0 && (
          <p className="text-center text-neutral-400 dark:text-neutral-500 text-sm py-10">No messages yet</p>
        )}
        {threads.map((t) => (
          <button
            key={t.id}
            onClick={() => onSelect(t.id)}
            className={`w-full text-left px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-colors ${
              activeId === t.id ? 'bg-primary-50 dark:bg-primary-900/20 border-l-2 border-l-primary-500' : ''
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <span className={`text-sm truncate ${t.unread > 0 ? 'font-semibold text-neutral-900 dark:text-neutral-100' : 'text-neutral-700 dark:text-neutral-300'}`}>
                {t.client?.firstName} {t.client?.lastName}
              </span>
              <span className="text-xs text-neutral-400 dark:text-neutral-500 shrink-0">{fmtTime(t.lastMessageAt)}</span>
            </div>
            <div className="flex items-center justify-between gap-2 mt-0.5">
              <div className="flex items-center gap-1.5 min-w-0">
                {t.source === 'contact_widget' && (
                  <span className="shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                    Contact Request
                  </span>
                )}
                <span className="text-xs text-neutral-500 dark:text-neutral-400 truncate">{t.subject}</span>
              </div>
              {t.unread > 0 && (
                <span className="shrink-0 inline-flex items-center justify-center h-5 w-5 rounded-full bg-primary-600 text-white text-xs font-medium">{t.unread}</span>
              )}
            </div>
            {t.preview && (
              <p className="text-xs text-neutral-400 dark:text-neutral-500 truncate mt-0.5">{t.preview}</p>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

async function downloadAttachment(url, fileName) {
  try {
    const res = await api.get(url, { responseType: 'blob' });
    const blobUrl = URL.createObjectURL(res.data);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(blobUrl);
  } catch {
    toast.error('Failed to download file');
  }
}

function MessageBubble({ msg, isOurs, threadId }) {
  const isClient = msg.senderRole === 'client';
  const senderName = isClient
    ? `${msg.sender?.firstName || ''} ${msg.sender?.lastName || ''}`.trim() || 'Client'
    : `${msg.sender?.firstName || ''} ${msg.sender?.lastName || ''}`.trim() || 'Care team';

  return (
    <div className={`flex ${isOurs ? 'justify-end' : 'justify-start'} mb-3`}>
      <div className={`max-w-[75%] ${isOurs ? 'order-last' : ''}`}>
        <div className={`text-xs text-neutral-400 dark:text-neutral-500 mb-1 ${isOurs ? 'text-right' : ''}`}>
          {senderName} · {format(parseISO(msg.createdAt), 'MMM d, h:mm a')}
        </div>
        <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-wrap break-words ${
          isOurs
            ? 'bg-primary-600 text-white rounded-br-sm'
            : 'bg-neutral-100 dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 rounded-bl-sm'
        }`}>
          {msg.content !== '(attachment)' && msg.content}
          {(msg.attachments || []).map((att, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => downloadAttachment(getAttachmentUrl(threadId, msg.id, idx), att.fileName)}
              className={`flex items-center gap-1.5 mt-1 text-xs underline ${isOurs ? 'text-primary-100' : 'text-primary-600 dark:text-primary-400'}`}
            >
              <PaperClipIcon className="h-3.5 w-3.5" />
              {att.fileName}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function ThreadView({ threadId, userId, onBack, onArchived }) {
  const queryClient = useQueryClient();
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState(null);

  const { data, isLoading } = useQuery({
    queryKey: ['message-thread', threadId],
    queryFn: () => getThread(threadId),
    refetchInterval: 15000,
  });

  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ['message-threads'] });
  }, [data, queryClient]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [data?.thread?.messages?.length]);

  const replyMutation = useMutation({
    mutationFn: () => replyToThread(threadId, text, file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['message-threads'] });
      setText('');
      setFile(null);
    },
    onError: (err) => toast.error(err.response?.data?.message || 'Failed to send'),
  });

  const archiveMutation = useMutation({
    mutationFn: () => archiveThread(threadId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['message-threads'] });
      toast.success('Thread archived');
      onArchived();
    },
    onError: () => toast.error('Failed to archive thread'),
  });

  const sendMessage = () => {
    if (!text.trim() && !file) return;
    replyMutation.mutate();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (isLoading) return <div className="flex justify-center items-center h-full"><Spinner /></div>;

  const { thread } = data;
  const messages = thread?.messages || [];

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-200 dark:border-neutral-700">
        <div className="flex items-center gap-3">
          <button onClick={onBack} className="md:hidden text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200">
            <ArrowLeftIcon className="h-5 w-5" />
          </button>
          <div>
            <p className="font-semibold text-neutral-900 dark:text-neutral-100 text-sm">{thread?.subject}</p>
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {thread?.client?.firstName} {thread?.client?.lastName}
            </p>
          </div>
        </div>
        <button
          onClick={() => archiveMutation.mutate()}
          disabled={archiveMutation.isPending}
          className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 transition-colors"
          title="Archive thread"
        >
          <ArchiveBoxIcon className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {messages.length === 0 && (
          <p className="text-center text-sm text-neutral-400 dark:text-neutral-500 py-8">No messages yet. Send the first one.</p>
        )}
        {messages.map((msg) => (
          <MessageBubble
            key={msg.id}
            msg={msg}
            isOurs={msg.senderId === userId || msg.senderRole !== 'client'}
            threadId={threadId}
          />
        ))}
        <div ref={bottomRef} />
      </div>

      {/* Compose */}
      <div className="border-t border-neutral-200 dark:border-neutral-700 px-4 py-3">
        {file && (
          <div className="flex items-center gap-2 mb-2 text-xs bg-neutral-100 dark:bg-neutral-700 rounded-lg px-3 py-1.5">
            <PaperClipIcon className="h-3.5 w-3.5 text-neutral-500" />
            <span className="flex-1 truncate text-neutral-700 dark:text-neutral-300">{file.name}</span>
            <button onClick={() => setFile(null)} className="text-neutral-400 hover:text-neutral-600">
              <XMarkIcon className="h-4 w-4" />
            </button>
          </div>
        )}
        <div className="flex items-end gap-2">
          <textarea
            className="flex-1 input text-sm resize-none py-2 min-h-[40px] max-h-32"
            rows={1}
            placeholder="Type a message… (Enter to send, Shift+Enter for newline)"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <button
            onClick={() => fileRef.current?.click()}
            className="p-2 rounded-lg text-neutral-400 hover:text-primary-600 hover:bg-neutral-100 dark:hover:bg-neutral-700 transition-colors"
            title="Attach file"
          >
            <PaperClipIcon className="h-5 w-5" />
          </button>
          <button
            onClick={sendMessage}
            disabled={(!text.trim() && !file) || replyMutation.isPending}
            className="p-2 rounded-lg bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50 transition-colors"
          >
            <PaperAirplaneIcon className="h-5 w-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function Messages() {
  // Get current user from auth store
  const [userId] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('sage-auth') || '{}');
      return stored?.state?.user?.id || null;
    } catch { return null; }
  });

  const [activeThreadId, setActiveThreadId] = useState(null);
  const [showNewThread, setShowNewThread] = useState(false);
  const [showList, setShowList] = useState(true);

  const { data, isLoading } = useQuery({
    queryKey: ['message-threads'],
    queryFn: () => getThreads({ archived: false }),
    refetchInterval: 30000,
  });

  const threads = data?.threads ?? [];

  const selectThread = (id) => {
    setActiveThreadId(id);
    setShowList(false);
  };

  return (
    <div className="h-[calc(100vh-64px)] flex flex-col">
      <div className="flex items-center justify-between mb-0 px-0 pt-0">
        {/* Page title is rendered inside the split layout */}
      </div>

      <div className="flex flex-1 overflow-hidden rounded-xl border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900">
        {/* Thread list — hidden on mobile when a thread is open */}
        <div className={`w-full md:w-72 lg:w-80 border-r border-neutral-200 dark:border-neutral-700 flex-shrink-0 flex flex-col ${activeThreadId && !showList ? 'hidden md:flex' : 'flex'}`}>
          {isLoading ? (
            <div className="flex justify-center py-10"><Spinner /></div>
          ) : (
            <ThreadList
              threads={threads}
              activeId={activeThreadId}
              onSelect={selectThread}
              onNewThread={() => setShowNewThread(true)}
            />
          )}
        </div>

        {/* Thread view */}
        <div className={`flex-1 ${!activeThreadId || showList ? 'hidden md:flex md:items-center md:justify-center' : 'flex flex-col'}`}>
          {!activeThreadId ? (
            <div className="text-center text-neutral-400 dark:text-neutral-500">
              <p className="text-sm">Select a thread to read messages</p>
            </div>
          ) : (
            <ThreadView
              key={activeThreadId}
              threadId={activeThreadId}
              userId={userId}
              onBack={() => { setShowList(true); setActiveThreadId(null); }}
              onArchived={() => { setActiveThreadId(null); setShowList(true); }}
            />
          )}
        </div>
      </div>

      <NewThreadModal
        isOpen={showNewThread}
        onClose={() => setShowNewThread(false)}
        onCreated={selectThread}
      />
    </div>
  );
}
